// Tracking service for logging Meta Pixel events to PocketBase
import { pb } from '@/lib/pocketbase';

export interface TrackingEvent {
  event_type: string;
  event_id?: string;
  user_id?: string;
  user_email?: string;
  page_url?: string;
  source: 'browser' | 'server';
  metadata?: Record<string, any>;
}

/**
 * Log a tracking event to PocketBase
 * Non-blocking - errors are caught and logged
 */
export const logTrackingEvent = async (event: TrackingEvent): Promise<void> => {
  try {
    await pb.collection('tracking_events').create({
      event_type: event.event_type,
      event_id: event.event_id || `${event.event_type}_${Date.now()}`,
      user_id: event.user_id || pb.authStore.model?.id || null,
      user_email: event.user_email || null,
      page_url: event.page_url || (typeof window !== 'undefined' ? window.location.href : null),
      source: event.source,
      metadata: event.metadata || {}
    });
  } catch (error) {
    // Non-blocking - just log the error
    console.error('Failed to log tracking event:', error);
  }
};

/**
 * Extract bookingId from a booking confirmation URL
 */
const extractBookingId = (url: string): string | null => {
  try {
    const urlObj = new URL(url);
    return urlObj.searchParams.get('bookingId');
  } catch {
    return null;
  }
};

/**
 * Fetch tutor name for a booking
 */
const fetchTutorNameForBooking = async (bookingId: string): Promise<string | null> => {
  try {
    const booking = await pb.collection('bookings').getOne(bookingId, {
      expand: 'tutorId',
      requestKey: `booking-tutor-${bookingId}-${Date.now()}`
    });
    return booking.expand?.tutorId?.name || null;
  } catch {
    return null;
  }
};

/**
 * Fetch tracking events with optional filters
 * For Purchase events without content_name, fetches tutor name from booking
 */
export const fetchTrackingEvents = async (options: {
  page?: number;
  perPage?: number;
  eventType?: string;
  source?: string;
  startDate?: string;
  endDate?: string;
  search?: string;
}) => {
  const { page = 1, perPage = 50, eventType, source, startDate, endDate, search } = options;
  
  const filters: string[] = [];
  
  if (eventType && eventType !== 'all') {
    filters.push(`event_type = "${eventType}"`);
  }
  
  if (source && source !== 'all') {
    filters.push(`source = "${source}"`);
  }
  
  if (startDate) {
    filters.push(`created >= "${startDate}"`);
  }
  
  if (endDate) {
    filters.push(`created <= "${endDate}"`);
  }
  
  if (search) {
    filters.push(`(event_id ~ "${search}" || user_id ~ "${search}" || page_url ~ "${search}")`);
  }
  
  const filter = filters.length > 0 ? filters.join(' && ') : '';
  
  const result = await pb.collection('tracking_events').getList(page, perPage, {
    filter,
    sort: '-created',
    requestKey: `tracking-events-${Date.now()}`
  });

  // Enrich Purchase events that don't have content_name with tutor name from booking
  const enrichedItems = await Promise.all(
    result.items.map(async (event) => {
      if (event.event_type === 'Purchase' && !event.metadata?.content_name && event.page_url) {
        const bookingId = extractBookingId(event.page_url);
        if (bookingId) {
          const tutorName = await fetchTutorNameForBooking(bookingId);
          if (tutorName) {
            return {
              ...event,
              metadata: {
                ...event.metadata,
                content_name: tutorName
              }
            };
          }
        }
      }
      return event;
    })
  );

  return {
    ...result,
    items: enrichedItems
  };
};

/**
 * Get tracking event statistics
 */
export const getTrackingStats = async () => {
  try {
    const [total, purchases, pageViews, addToCarts] = await Promise.all([
      pb.collection('tracking_events').getList(1, 1, { requestKey: 'stats-total' }),
      pb.collection('tracking_events').getList(1, 1, { 
        filter: 'event_type = "Purchase"',
        requestKey: 'stats-purchases'
      }),
      pb.collection('tracking_events').getList(1, 1, { 
        filter: 'event_type = "PageView"',
        requestKey: 'stats-pageviews'
      }),
      pb.collection('tracking_events').getList(1, 1, { 
        filter: 'event_type = "AddToCart"',
        requestKey: 'stats-addtocart'
      })
    ]);
    
    return {
      total: total.totalItems,
      purchases: purchases.totalItems,
      pageViews: pageViews.totalItems,
      addToCarts: addToCarts.totalItems
    };
  } catch (error) {
    console.error('Failed to fetch tracking stats:', error);
    return { total: 0, purchases: 0, pageViews: 0, addToCarts: 0 };
  }
};

/**
 * Get traffic analytics by tutor and page with optional time filter
 */
export const getTrafficAnalytics = async (timeFilter?: 'today' | 'yesterday' | 'week' | 'month' | 'all') => {
  try {
    // Calculate date filter based on timeFilter
    let dateFilter = '';
    const now = new Date();
    
    if (timeFilter && timeFilter !== 'all') {
      let startDate: Date;
      
      switch (timeFilter) {
        case 'today':
          startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
          break;
        case 'yesterday':
          startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1);
          const endOfYesterday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
          dateFilter = `created >= "${startDate.toISOString()}" && created < "${endOfYesterday.toISOString()}"`;
          break;
        case 'week':
          startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 7);
          break;
        case 'month':
          startDate = new Date(now.getFullYear(), now.getMonth() - 1, now.getDate());
          break;
        default:
          startDate = new Date(0);
      }
      
      if (timeFilter !== 'yesterday') {
        dateFilter = `created >= "${startDate.toISOString()}"`;
      }
    }

    // Fetch events with date filter
    const result = await pb.collection('tracking_events').getList(1, 1000, {
      filter: dateFilter,
      sort: '-created',
      requestKey: `traffic-analytics-${Date.now()}`
    });

    const tutorTraffic: Record<string, { id: string; count: number }> = {};
    const pageTraffic: Record<string, number> = {};

    for (const event of result.items) {
      // Extract tutor ID from page_url for tutor detail pages
      if (event.page_url) {
        try {
          const url = new URL(event.page_url);
          const path = url.pathname;
          
          // Check if it's a tutor detail page: /tutors/:id or /tutor/:id
          const tutorMatch = path.match(/^\/tutors?\/([^/?]+)/);
          if (tutorMatch) {
            const tutorId = tutorMatch[1];
            // Skip admin pages and other non-tutor paths
            if (tutorId && tutorId !== 'admin' && !tutorId.includes('.')) {
              if (!tutorTraffic[tutorId]) {
                tutorTraffic[tutorId] = { id: tutorId, count: 0 };
              }
              tutorTraffic[tutorId].count++;
            }
          }
          
          // Aggregate page traffic
          pageTraffic[path] = (pageTraffic[path] || 0) + 1;
        } catch {
          // Invalid URL, skip
        }
      }
    }

    // Get top 10 tutor IDs sorted by count
    const topTutorIds = Object.values(tutorTraffic)
      .sort((a, b) => b.count - a.count)
      .slice(0, 10);

    // Fetch tutor names from PocketBase
    const topTutors: { name: string; count: number }[] = [];
    for (const tutor of topTutorIds) {
      try {
        const tutorRecord = await pb.collection('tutors').getOne(tutor.id, {
          fields: 'name',
          requestKey: `tutor-name-${tutor.id}-${Date.now()}`
        });
        topTutors.push({ name: tutorRecord.name || tutor.id, count: tutor.count });
      } catch {
        // Tutor not found, use ID as fallback
        topTutors.push({ name: tutor.id, count: tutor.count });
      }
    }

    const topPages = Object.entries(pageTraffic)
      .map(([path, count]) => ({ path, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 10);

    return { topTutors, topPages };
  } catch (error) {
    console.error('Failed to fetch traffic analytics:', error);
    return { topTutors: [], topPages: [] };
  }
};

/**
 * Export all tracking events for backup
 */
export const exportAllTrackingEvents = async (): Promise<any[]> => {
  const allEvents: any[] = [];
  let page = 1;
  const perPage = 500;
  
  while (true) {
    const result = await pb.collection('tracking_events').getList(page, perPage, {
      sort: '-created',
      requestKey: `export-${page}-${Date.now()}`
    });
    
    allEvents.push(...result.items);
    
    if (page >= result.totalPages) break;
    page++;
  }
  
  return allEvents;
};

/**
 * Delete all tracking events (wipe)
 * Uses parallel deletion for better performance
 */
export const deleteAllTrackingEvents = async (onProgress?: (deleted: number, total: number) => void): Promise<number> => {
  let deletedCount = 0;
  
  // First get total count
  const initial = await pb.collection('tracking_events').getList(1, 1, {
    requestKey: `delete-count-${Date.now()}`
  });
  const totalItems = initial.totalItems;
  
  if (totalItems === 0) return 0;
  
  // Fetch and delete in batches with parallel deletion
  while (true) {
    const result = await pb.collection('tracking_events').getList(1, 50, {
      requestKey: `delete-batch-${Date.now()}`
    });
    
    if (result.items.length === 0) break;
    
    // Delete items in parallel (batch of 10 at a time to avoid overwhelming)
    const batchSize = 10;
    for (let i = 0; i < result.items.length; i += batchSize) {
      const batch = result.items.slice(i, i + batchSize);
      await Promise.all(
        batch.map(item => 
          pb.collection('tracking_events').delete(item.id).catch(err => {
            console.error('Failed to delete event:', item.id, err);
          })
        )
      );
      deletedCount += batch.length;
      onProgress?.(deletedCount, totalItems);
    }
  }
  
  return deletedCount;
};
